// Pure helpers for the admin finance dashboard. Nothing here invents a number: a missing value renders as "Belum tersedia".
export const UNKNOWN = "Belum tersedia";

export const formatIdr = (value) => {
  if (value === null || value === undefined || value === "") return UNKNOWN;
  const n = Number(value);
  if (!Number.isFinite(n)) return UNKNOWN;
  return `${n < 0 ? "-" : ""}Rp${Math.abs(Math.round(n)).toLocaleString("id-ID")}`;
};
export const formatPercent = (value) => (value === null || value === undefined || value === "" || !Number.isFinite(Number(value)) ? UNKNOWN : `${Number(value).toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`);
export const formatNumber = (value) => (value === null || value === undefined || !Number.isFinite(Number(value)) ? UNKNOWN : Number(value).toLocaleString("id-ID"));
// Provider units are tenths of a KIE credit: show as KIE credits, never as internal credits.
export const formatProviderUnits = (tenths) => (Number.isInteger(tenths) ? `${(tenths / 10).toLocaleString("id-ID", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kredit KIE` : UNKNOWN);

export const BASIS_LABELS = { actual: "Aktual", estimate: "Estimasi", incomplete: "Belum lengkap", unreconciled: "Belum direkonsiliasi" };
export const basisLabel = (basis) => BASIS_LABELS[basis] || "Tidak diketahui";

export const KIND_LABELS = {
  package_purchase: "Pembelian paket", credit_grant: "Kredit masuk", reservation: "Reservasi kredit", settlement: "Settlement kredit", release: "Pelepasan reservasi",
  refund: "Refund", provider_cost: "Biaya provider", provider_purchase: "Pembelian kredit provider", operating_cost: "Biaya operasional", adjustment: "Penyesuaian",
};
export const kindLabel = (kind) => KIND_LABELS[kind] || "Lainnya";

const pad = (n) => String(n).padStart(2, "0");
const ymd = (date) => `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
// Default window: the last `days` days ending today (UTC), inclusive.
export const defaultRange = (now = new Date(), days = 30) => {
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const start = new Date(end.getTime() - (days - 1) * 86400000);
  return { start: ymd(start), end: ymd(end) };
};
export const validRange = (start, end) => /^\d{4}-\d{2}-\d{2}$/.test(start || "") && /^\d{4}-\d{2}-\d{2}$/.test(end || "") && start <= end
  && (Date.parse(end) - Date.parse(start)) / 86400000 + 1 <= 366;

// Bars for the chart. Unknown values stay unknown (no bar, no zero). The scale is shared so bars are comparable.
export const chartModel = (points, keys) => {
  const list = Array.isArray(points) ? points : [];
  const values = list.flatMap((p) => keys.map((k) => (typeof p[k] === "number" ? p[k] : null)).filter((v) => v !== null));
  const max = Math.max(0, ...values);
  const min = Math.min(0, ...values);
  const span = max - min || 1;
  return {
    empty: list.length === 0,
    bars: list.map((p) => ({ bucket: p.bucket, series: keys.map((k) => {
      const v = typeof p[k] === "number" ? p[k] : null;
      return { key: k, value: v, known: v !== null, height: v === null ? 0 : Math.abs(v) / span, negative: v !== null && v < 0 };
    }) })),
    max, min,
  };
};

export const partialWarning = (report) => {
  if (!report || !report.partial) return "";
  const names = { recognized_revenue: "pendapatan diakui", provider_cost: "biaya provider", gateway_fees: "biaya gateway", taxes: "pajak", operating_costs: "biaya operasional" };
  const missing = (report.missing_components || []).map((c) => names[c] || c);
  return `Angka belum lengkap — bukan laba bersih akuntansi. Komponen belum diketahui: ${missing.join(", ") || "-"}.`;
};

export const errorText = (error) => (typeof error?.response?.data?.detail === "string" ? error.response.data.detail : "Permintaan belum berhasil. Coba lagi.");

export const recordFormValid = (kind, form) => {
  const filled = (...names) => names.every((n) => typeof form[n] === "string" && form[n].trim() !== "");
  if (kind === "fx") return filled("currency", "rate", "effective_date", "source");
  if (kind === "purchase") return filled("provider", "units", "currency", "amount", "purchased_at");
  if (kind === "opcost") return filled("category", "amount_idr", "cost_date");
  if (kind === "adjustment") return filled("kind", "amount_idr", "effective_date", "reason") && form.reason.trim().length >= 5;
  return false;
};

// A8: any report that does not cover every document must say so, loudly and persistently. Returns "" only when EVERY report is complete.
export const incompleteNotice = (...reports) => {
  const sources = new Set();
  let limit = null;
  let any = false;
  for (const r of reports) {
    const c = r && r.completeness;
    if (!c) { any = true; continue; }                      // a report without a completeness block is treated as unknown, not as complete
    if (c.complete !== true) { any = true; (c.truncated_sources || []).forEach((s) => sources.add(s)); limit = c.document_limit || limit; }
  }
  if (!any) return "";
  const list = [...sources].join(", ");
  return `Data tidak lengkap${list ? `: sumber ${list} melebihi batas ${limit ?? "dokumen"} dokumen` : ""}. Angka yang tampil hanya sebagian dan BUKAN total. Persempit rentang tanggal atau minta agregasi penuh sebelum dipakai.`;
};
export const reconcileLabel = (status) => (status === "clean" ? "Cocok" : status === "incomplete" ? "Tidak lengkap (data terpotong)" : "Ada selisih");

// Figures that came from a MongoDB aggregation cover all the documents, but the aggregation's verification status is a fact owned by the server
// (which versions it was checked on). The UI shows the server's own statement and never hard-codes a verification claim. "" when none was used.
export const aggregationNotice = (...reports) => {
  const used = new Set();
  let note = "";
  for (const r of reports) {
    const c = r && r.completeness;
    if (c && Array.isArray(c.exact_by_aggregation) && c.exact_by_aggregation.length) {
      c.exact_by_aggregation.forEach((name) => used.add(name));
      if (!note && typeof c.aggregation_note === "string") note = c.aggregation_note.replace(/^Sumber [^.]*\. /, "");
    }
  }
  if (!used.size) return "";
  return `Sumber ${[...used].join(", ")} dihitung lewat agregasi MongoDB karena melebihi batas dokumen. Angkanya mencakup seluruh data. ${note || "Status verifikasi agregasi tidak diketahui."}`;
};
